import { AreaDict } from './index';

/**
 * Property-area translations.
 *
 * Covers PropertyView (pv*), PortfolioView (pfv*), MainDashboard (md*),
 * AppHeader (ah*), the Asset Desk panels (ad*), InstallmentPlanView (ipl*)
 * and PartnerView (ptn*).
 *
 * Un-prefixed keys at the bottom are NOT new names: PropertyView already
 * called t() with them, but they existed in no dictionary, so t() fell through
 * to its last resort and printed the raw key ("constructionStarted") on screen.
 * They are defined here under the names the component already uses.
 */
export const propertyDict: AreaDict = {
  en: {
    // ---------- PropertyView ----------
    pvSold: 'SOLD',
    pvDldProjectNo: 'DLD Project',
    pvPerDocument: 'per',
    pvConstructionCompleted: 'Construction Completed',
    pvConstructionProgress: 'Construction progress',
    pvLatestUpdate: 'Latest update',
    pvPassword: 'Password',
    pvDldMarketValue: 'DLD Market Value',
    pvDldPricePerSqm: 'DLD Price/m²',
    pvDldAvgRent: 'DLD Avg Rent',
    pvContracts: 'contracts',
    pvComparables: 'comparables',
    // Comma grouping is intentional in both languages: the price parser strips
    // commas only, so a German "3.000.000" hint would invite unparseable input.
    pvEgPriceOriginal: 'e.g. 3,000,000',
    pvEgPriceSelling: 'e.g. 2,600,000',
    pvYieldSuffix: 'yield',
    pvLoadingDldData: 'Loading DLD market data...',
    pvMarketDataFromDld: 'Market data from Dubai Land Department official records',
    pvComparableTransactions: 'comparable transactions',
    pvBasedOnEjari: 'Based on DLD Ejari contracts',
    pvCurrentRentalData: 'Current rental data',
    pvDldData: 'DLD Data',
    pvGrossYield: 'Gross Yield',
    pvNetYieldEst: 'Net Yield (est.)',
    pvAfterExpenses: 'After ~15% expenses',
    pvPriceToRent: 'Price to Rent',
    pvYearsToRecoup: 'Years to recoup',
    pvDldComparableRentals: 'DLD Comparable Rental Contracts',
    pvColStartDate: 'Start Date',
    pvColSize: 'Size',
    pvRenewalTag: '(Renewal)',
    pvNewTag: '(New)',
    pvRentalDataUnavailable: 'Rental Data Unavailable',
    pvRentalDataUnavailableDesc: 'DLD rental data for this property will appear once Ejari contracts are available in the database. Sync market data to check for updates.',
    pvUploadOqoodNote: 'Upload OQOOD, OTP, or SPA to update your property data',
    pvMissingDocsTitle: 'Please upload the following documents',
    pvMissingDocsDesc: 'With these on file you get the most out of the app: accurate value, payment plan and returns for this unit.',
    pvMissingDocOqood: 'Oqood / Title Deed',
    pvMissingDocSpa: 'SPA / OTP',
    pvMissingDocSoa: 'SOA',
    pvMissingDocsBtn: 'Upload documents',
    pvRejected: 'Rejected',
    pvSnaggingReport: 'Snagging Report',
    pvDefect: 'defect',
    pvDefects: 'defects',
    pvFound: 'found',
    pvSevCritical: 'Critical',
    pvSevMajor: 'Major',
    pvSevMinor: 'Minor',
    pvSevCosmetic: 'Cosmetic',
    pvKeyIssues: 'Key Issues:',
    pvInspector: 'Inspector',
    pvCreateFolderTitle: 'Create a property folder',
    pvCreateFolderDesc: 'This creates a folder in Google Drive and Firebase. After creating, use "Add Documents" to upload OQOOD, SPA, OTP, etc.',
    pvReady: 'Ready',
    pvPropertyCreated: '✓ Property created! You can now close this and use "Add Documents" to upload files.',
    pvFailedToCreateUnit: 'Failed to create unit',
    pvUnitCreatedRefresh: 'Unit created successfully! Please refresh the page to see it.',
    pvDocBookingForm: 'Booking Form',
    pvDocReservationAgreement: 'Reservation Agreement',
    pvDocMoa: 'MOA (Memorandum of Association)',
    pvDocPoa: 'Power of Attorney (POA)',
    pvDocCertIncorporation: 'Certificate of Incorporation',
    pvDocShareCertificate: 'Share Certificate',
    pvDocBoardResolution: 'Board Resolution',
    pvDocTaxCertificate: 'Tax Certificate',
    pvDocBankReference: 'Bank Reference Letter',
    pvDocBrochure: 'Brochure',
    pvDocPropertyBrochure: 'Property Brochure',
    pvDocOtherDocuments: 'Other Documents',
    pvFileFormats: 'PDF, JPG, PNG (Max 10MB)',
    pvRequestDocsSub: 'Request documents for your property',
    pvSelectPropertyUnit: 'Select Property/Unit',
    pvNoPropsAddFirst: 'No properties found. Please add a property first.',
    pvSelectDocsNeeded: 'Select Documents Needed',
    pvSubmitRequest: 'Submit Request',
    pvAlertSelectUnit: 'Please select a property/unit',
    pvAlertSelectDocType: 'Please select at least one document type',
    pvDocRequestSuccess: 'Document request submitted successfully! Our team will contact you soon.',
    pvDocRequestError: 'Error submitting request. Please try again or contact support.',
    pvErrorSendingRequest: 'Error sending request',
    pvCouldNotOpenDoc: 'Could not open document',
    pvCouldNotDownloadDoc: 'Could not download document',
    pvCouldNotSaveDoc: 'Could not save document',
    pvSavedToDevice: 'Saved to device:',
    pvPleaseLogIn: 'Please log in to access documents.',

    // ---------- PortfolioView ----------
    pfvReport: 'Report',
    pfvPerformance: 'Performance',
    pfvCalculator: 'Calculator',
    pfvUploadOtpFirst: 'Upload an OTP document to add your first property.',
    pfvChooseProperty: 'Choose a property to open its valuation report',
    pfvEstConfHigh: 'est. · high confidence',
    pfvEstConfMedium: 'est. · medium confidence',
    pfvEstConfLow: 'est. · low confidence',
    pfvPurchasePriceNote: 'purchase price',
    pfvUpdatedAuto: 'Updated automatically · Last update:',
    pfvSourceDld: 'Source: Dubai Land Department',
    pfvPerformanceOverview: 'Performance Overview',
    pfvPortfolioRoi: 'Portfolio ROI',
    pfvPropertyBreakdown: 'Property Breakdown',
    pfvNoPropsToAnalyze: 'No properties to analyze',
    pfvGain: 'Gain',
    pfvOfPortfolio: 'of portfolio',
    pfvDldMarketInsights: 'DLD Market Insights',
    pfvDldPortfolioValue: 'DLD Portfolio Value',
    pfvDldAnnualRental: 'DLD Annual Rental',
    pfvGrossRentalYield: 'Gross Rental Yield',
    pfvMarketInsightsPending: 'Market insights appear automatically once DLD data covers this portfolio.',

    // ---------- MainDashboard ----------
    mdProjectVideo: 'Project video',
    mdVideoUnavailable: 'Video unavailable',
    mdVideoNotSupported: 'Your browser does not support the video tag.',
    mdOpenChat: 'Open chat',
    mdOpenLink: 'Open Link',
    mdView: 'View',
    mdDistressSale: 'Distress Sale',

    // ---------- AppHeader ----------
    ahContactWhatsApp: 'Contact us on WhatsApp',
    ahNoNotifications: 'No notifications yet',
    ahAppUpdate: 'App Update',
    ahJustNow: 'Just now',
    ahMinutesAgo: '{n}m ago',
    ahHoursAgo: '{n}h ago',
    ahDaysAgo: '{n}d ago',

    // ---------- Asset Desk panels ----------
    adTierBuilding: 'Direct DLD comps · Building',
    adTierProject: 'Direct DLD comps · Project',
    adTierArea: 'Wider DLD comp set · Area',
    adTierBedroom: 'Wider DLD comp set · Bedroom',
    adTierInsufficient: 'Insufficient DLD data',
    adMethodology: 'Methodology',
    adMarketMethodologyTip: 'Computed from DLD transactions only. No estimates. Median price per sqft of bedroom- and size-matched secondary-market sales, after 1.5×IQR outlier filtering.',
    adRentalMethodologyTip: 'Computed from DLD Ejari contracts only. No estimates. Median rent per sqft of residential, bedroom- and size-matched contracts, after 1.5×IQR outlier filtering, multiplied by subject unit sqft.',
    adMarketValue: 'Market Value',
    adMarketLoadFailed: 'Failed to load market data',
    adRentalValue: 'Rental Value',
    adRentalLoadFailed: 'Failed to load rental data',
    adEstimateWithheld: 'Estimate withheld — insufficient DLD data',
    adEstimateWithheldYield: 'Estimate withheld — yield anomaly (>10%), likely data error',
    adVsPurchasePrice: 'vs purchase price',
    adUnrealizedGain: 'Unrealized Gain',
    adBasedOn: 'Based on',
    adTransaction: 'transaction',
    adTransactions: 'transactions',
    adEjariContract: 'Ejari contract',
    adEjariContracts: 'Ejari contracts',
    adRecentComparables: 'Recent Comparables',
    adRecentEjari: 'Recent Ejari Contracts',
    adColDate: 'Date',
    adColPriceAed: 'Price (AED)',
    adColType: 'Type',
    adColMonthly: 'Monthly',
    adColAnnual: 'Annual',
    adOffPlan: 'Off-plan',
    adRenewal: 'Renewal',
    adSourceDld: 'Source: Dubai Land Department · DLD-only methodology · No manual or external estimates',
    adSourceEjari: 'Source: DLD Ejari · DLD-only methodology · No manual or external estimates',
    adMinPerYr: 'Min/yr',
    adMedianPerYr: 'Median/yr',
    adMaxPerYr: 'Max/yr',
    adRentPerSqftYr: 'Rent/sqft/yr',
    adImpliedGrossYield: 'Implied gross yield',
    adPerMonth: '/mo',
    adPerYear: '/yr',
    adYoY: 'YoY',
    adNoDldDataForUnit: 'No DLD data available for this unit',
    adNoMatchingEjari: 'No matching DLD Ejari contracts',
    adProjectTimeline: 'Project Timeline',
    adTimelineLoadFailed: 'Failed to load project timeline',
    adTimelineUnavailable: 'Project timeline data is not available. This may be because the project is not registered in the DLD Mashrooi system.',
    adComplete: 'complete',
    adProjectNo: 'Project #',
    adTotalArea: 'Total Area',
    adEscrow: 'Escrow',
    adSourceMashrooi: 'Source: DLD Mashrooi · Project:',

    // ---------- Benchmark: how this unit compares ----------
    bmTitle: 'How this unit compares',
    bmSubtitlePre: 'Registered DLD sales, last',
    bmSubtitlePost: 'months',
    bmUnavailable: 'No registered sales available to compare against yet.',
    bmThisUnit: 'This unit',
    bmBuilding: 'This building',
    bmCommunity: 'Community',
    bmFromValuation: 'from the DLD valuation',
    bmFromPurchase: 'from your purchase price — no DLD valuation yet',
    bmSale: 'sale',
    bmSales: 'sales',
    bmBasisBeds: 'same bedroom count',
    bmBasisAllTypes: 'all unit types',
    bmTooFew: 'too few registered sales to compare',
    bmVerdictAbove: 'above the building median',
    bmVerdictBelow: 'below the building median',
    bmVerdictInLine: 'In line with the building',
    bmVsBuilding: 'on price per sqft',
    bm3mVs3m: 'last 3 months vs previous 3',
    bmOffplanNote: 'Compared with off-plan sales only.',
    bmReadyNote: 'Compared with completed-property sales only.',
    bmMinCompsPre: 'A median is shown only from',
    bmMinCompsPost: 'sales or more.',
    bmEarnsTitle: 'What it earns',
    bmGrossYield: 'Gross yield',
    bmNetYield: 'Net yield',
    bmAfterServiceCharge: 'Net is after the service charge of',
    bmNoServiceCharge: 'No service charge on record for this building, so net yield is not shown.',
    bmYear: 'yr',
    bmSource: 'source',
    bmLiquidityTitle: 'How often it trades',
    bmInThisBuilding: 'in this building over 12 months',
    bmPerQuarter: 'per quarter',
    bmInTheCommunity: 'in the community',
    bmAreaRentTitle: 'What the community rents at',
    bmEjariContracts: 'Ejari contracts',
    bmSupplyTitle: 'New supply coming to this community',
    bmSupplyUnits: 'units',
    bmSupplyAcross: 'across',
    bmSupplyProject: 'registered project',
    bmSupplyProjects: 'registered projects',
    bmSupplyWithin: 'due to complete within',
    bmSupplyNext: 'Soonest',
    adMilestoneBookingStarted: 'Booking Started',
    adMilestoneConstructionStarted: 'Construction Started',
    adMilestoneConstructionProgress: 'Construction Progress',
    adMilestoneExpectedCompletion: 'Expected Completion',
    adValuationTitle: 'Asset Desk Valuation',
    adPoweredByDld: 'Powered by Dubai Land Department official records',
    adRefresh: 'Refresh',

    // ---------- InstallmentPlanView ----------
    iplTotal: 'Total',
    iplMonth: 'Month',
    iplTapStatusToChange: 'Tap status to change',
    iplTapToChangeStatus: 'Tap to change status',
    iplPastPaymentsSummary: 'Past Payments Summary',

    // ---------- PartnerView ----------
    ptnVvipCooperationPartner: 'VVIP - Cooperation Partner',
    ptnCommissionStatement: 'Commission Statement',
    ptnSalutationMr: 'Mr',
    ptnSalutationMs: 'Ms',
    ptnSalutationMrs: 'Mrs',
    ptnSalutationDr: 'Dr',

    // ---------- keys PropertyView already referenced but no dictionary defined ----------
    notifyWhenReady: 'We\'ll notify you when this feature is ready.',
    propertySize: 'Property Size',
    projectTimeline: 'Project Timeline',
    bookingStarted: 'Booking Started',
    constructionStarted: 'Construction Started',
    expectedCompletion: 'Expected Completion',
    pricePerSqft: 'Price/Area',
    exchangeRateNote: 'EUR values are approximate (1 AED ≈ 0.25 EUR). Actual rates may vary.',
    unitDocs: 'Unit',
    requestNewUnit: 'Request New Unit',
    availableDoc: 'Which document do you have?',
    selectDocument: '-- Select Document --',
    additionalNotes: 'Additional Notes (optional)',
    notesPlaceholder: 'e.g. Developer: Avenew Development, 2BR apartment',
    selectDocType: 'Please select which document you have',
    sendRequest: 'Send Request',
  },

  de: {
    // ---------- PropertyView ----------
    pvSold: 'VERKAUFT',
    pvDldProjectNo: 'DLD-Projekt',
    pvPerDocument: 'laut',
    pvConstructionCompleted: 'Bau abgeschlossen',
    pvConstructionProgress: 'Baufortschritt',
    pvLatestUpdate: 'Letzte Aktualisierung',
    pvPassword: 'Passwort',
    pvDldMarketValue: 'DLD-Marktwert',
    pvDldPricePerSqm: 'DLD-Preis/m²',
    pvDldAvgRent: 'DLD-Durchschnittsmiete',
    pvContracts: 'Verträge',
    pvComparables: 'Vergleichswerte',
    pvEgPriceOriginal: 'z. B. 3,000,000',
    pvEgPriceSelling: 'z. B. 2,600,000',
    pvYieldSuffix: 'Rendite',
    pvLoadingDldData: 'DLD-Marktdaten werden geladen...',
    pvMarketDataFromDld: 'Marktdaten aus den offiziellen Registern des Dubai Land Department',
    pvComparableTransactions: 'vergleichbare Transaktionen',
    pvBasedOnEjari: 'Basierend auf DLD-Ejari-Verträgen',
    pvCurrentRentalData: 'Aktuelle Mietdaten',
    pvDldData: 'DLD-Daten',
    pvGrossYield: 'Bruttorendite',
    pvNetYieldEst: 'Nettorendite (gesch.)',
    pvAfterExpenses: 'Nach ca. 15 % Kosten',
    pvPriceToRent: 'Kaufpreis-Miet-Verhältnis',
    pvYearsToRecoup: 'Jahre bis zur Amortisation',
    pvDldComparableRentals: 'Vergleichbare DLD-Mietverträge',
    pvColStartDate: 'Mietbeginn',
    pvColSize: 'Größe',
    pvRenewalTag: '(Verlängerung)',
    pvNewTag: '(Neu)',
    pvRentalDataUnavailable: 'Keine Mietdaten verfügbar',
    pvRentalDataUnavailableDesc: 'DLD-Mietdaten zu dieser Immobilie erscheinen, sobald Ejari-Verträge in der Datenbank vorliegen. Synchronisieren Sie die Marktdaten, um nach Aktualisierungen zu suchen.',
    pvUploadOqoodNote: 'Laden Sie OQOOD, OTP oder SPA hoch, um die Daten Ihrer Immobilie zu aktualisieren',
    pvMissingDocsTitle: 'Bitte laden Sie folgende Dokumente hoch',
    pvMissingDocsDesc: 'Damit holen Sie das Beste aus der App heraus: genauer Wert, Zahlungsplan und Rendite für diese Einheit.',
    pvMissingDocOqood: 'Oqood / Eigentumsurkunde',
    pvMissingDocSpa: 'SPA / OTP',
    pvMissingDocSoa: 'SOA',
    pvMissingDocsBtn: 'Dokumente hochladen',
    pvRejected: 'Abgelehnt',
    pvSnaggingReport: 'Mängelbericht',
    pvDefect: 'Mangel',
    pvDefects: 'Mängel',
    pvFound: 'festgestellt',
    pvSevCritical: 'Kritisch',
    pvSevMajor: 'Schwerwiegend',
    pvSevMinor: 'Gering',
    pvSevCosmetic: 'Kosmetisch',
    pvKeyIssues: 'Wesentliche Mängel:',
    pvInspector: 'Prüfer',
    pvCreateFolderTitle: 'Immobilienordner erstellen',
    pvCreateFolderDesc: 'Dies erstellt einen Ordner in Google Drive und Firebase. Nutzen Sie danach „Dokumente hinzufügen“, um OQOOD, SPA, OTP usw. hochzuladen.',
    pvReady: 'Bezugsfertig',
    pvPropertyCreated: '✓ Immobilie erstellt! Sie können dieses Fenster nun schließen und über „Dokumente hinzufügen“ Dateien hochladen.',
    pvFailedToCreateUnit: 'Einheit konnte nicht erstellt werden',
    pvUnitCreatedRefresh: 'Einheit erfolgreich erstellt! Bitte laden Sie die Seite neu, um sie zu sehen.',
    pvDocBookingForm: 'Buchungsformular',
    pvDocReservationAgreement: 'Reservierungsvereinbarung',
    pvDocMoa: 'MOA (Gesellschaftsvertrag)',
    pvDocPoa: 'Vollmacht (POA)',
    pvDocCertIncorporation: 'Gründungsurkunde',
    pvDocShareCertificate: 'Anteilszertifikat',
    pvDocBoardResolution: 'Gesellschafterbeschluss',
    pvDocTaxCertificate: 'Steuerbescheinigung',
    pvDocBankReference: 'Bankreferenzschreiben',
    pvDocBrochure: 'Broschüre',
    pvDocPropertyBrochure: 'Immobilienbroschüre',
    pvDocOtherDocuments: 'Sonstige Dokumente',
    pvFileFormats: 'PDF, JPG, PNG (max. 10 MB)',
    pvRequestDocsSub: 'Dokumente für Ihre Immobilie anfordern',
    pvSelectPropertyUnit: 'Immobilie/Einheit auswählen',
    pvNoPropsAddFirst: 'Keine Immobilien gefunden. Bitte fügen Sie zuerst eine Immobilie hinzu.',
    pvSelectDocsNeeded: 'Benötigte Dokumente auswählen',
    pvSubmitRequest: 'Anfrage senden',
    pvAlertSelectUnit: 'Bitte wählen Sie eine Immobilie/Einheit aus',
    pvAlertSelectDocType: 'Bitte wählen Sie mindestens einen Dokumenttyp aus',
    pvDocRequestSuccess: 'Dokumentenanfrage erfolgreich gesendet! Unser Team wird sich in Kürze bei Ihnen melden.',
    pvDocRequestError: 'Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut oder wenden Sie sich an den Support.',
    pvErrorSendingRequest: 'Fehler beim Senden der Anfrage',
    pvCouldNotOpenDoc: 'Dokument konnte nicht geöffnet werden',
    pvCouldNotDownloadDoc: 'Dokument konnte nicht heruntergeladen werden',
    pvCouldNotSaveDoc: 'Dokument konnte nicht gespeichert werden',
    pvSavedToDevice: 'Auf dem Gerät gespeichert:',
    pvPleaseLogIn: 'Bitte melden Sie sich an, um auf Dokumente zuzugreifen.',

    // ---------- PortfolioView ----------
    pfvReport: 'Bericht',
    pfvPerformance: 'Entwicklung',
    pfvCalculator: 'Rechner',
    pfvUploadOtpFirst: 'Laden Sie ein OTP-Dokument hoch, um Ihre erste Immobilie hinzuzufügen.',
    pfvChooseProperty: 'Wählen Sie eine Immobilie, um deren Bewertungsbericht zu öffnen',
    pfvEstConfHigh: 'gesch. · hohe Konfidenz',
    pfvEstConfMedium: 'gesch. · mittlere Konfidenz',
    pfvEstConfLow: 'gesch. · geringe Konfidenz',
    pfvPurchasePriceNote: 'Kaufpreis',
    pfvUpdatedAuto: 'Automatisch aktualisiert · Letzte Aktualisierung:',
    pfvSourceDld: 'Quelle: Dubai Land Department',
    pfvPerformanceOverview: 'Übersicht der Wertentwicklung',
    pfvPortfolioRoi: 'Portfolio-ROI',
    pfvPropertyBreakdown: 'Aufschlüsselung nach Immobilie',
    pfvNoPropsToAnalyze: 'Keine Immobilien zur Auswertung',
    pfvGain: 'Gewinn',
    pfvOfPortfolio: 'des Portfolios',
    pfvDldMarketInsights: 'DLD-Markteinblicke',
    pfvDldPortfolioValue: 'DLD-Portfoliowert',
    pfvDldAnnualRental: 'DLD-Jahresmiete',
    pfvGrossRentalYield: 'Brutto-Mietrendite',
    pfvMarketInsightsPending: 'Markteinblicke erscheinen automatisch, sobald DLD-Daten für dieses Portfolio vorliegen.',

    // ---------- MainDashboard ----------
    mdProjectVideo: 'Projektvideo',
    mdVideoUnavailable: 'Video nicht verfügbar',
    mdVideoNotSupported: 'Ihr Browser unterstützt das Video-Tag nicht.',
    mdOpenChat: 'Chat öffnen',
    mdOpenLink: 'Link öffnen',
    mdView: 'Ansehen',
    mdDistressSale: 'Notverkauf',

    // ---------- AppHeader ----------
    ahContactWhatsApp: 'Kontaktieren Sie uns über WhatsApp',
    ahNoNotifications: 'Noch keine Benachrichtigungen',
    ahAppUpdate: 'App-Update',
    ahJustNow: 'Gerade eben',
    ahMinutesAgo: 'vor {n} Min.',
    ahHoursAgo: 'vor {n} Std.',
    ahDaysAgo: 'vor {n} Tagen',

    // ---------- Asset Desk panels ----------
    adTierBuilding: 'Direkte DLD-Vergleichswerte · Gebäude',
    adTierProject: 'Direkte DLD-Vergleichswerte · Projekt',
    adTierArea: 'Erweiterte DLD-Vergleichswerte · Gebiet',
    adTierBedroom: 'Erweiterte DLD-Vergleichswerte · Schlafzimmer',
    adTierInsufficient: 'Unzureichende DLD-Daten',
    adMethodology: 'Methodik',
    adMarketMethodologyTip: 'Ausschließlich aus DLD-Transaktionen berechnet. Keine Schätzungen. Median-Preis pro sqft aus Zweitmarktverkäufen mit übereinstimmender Schlafzimmerzahl und Größe, nach Ausreißerfilterung (1,5×IQR).',
    adRentalMethodologyTip: 'Ausschließlich aus DLD-Ejari-Verträgen berechnet. Keine Schätzungen. Median-Miete pro sqft aus Wohnmietverträgen mit übereinstimmender Schlafzimmerzahl und Größe, nach Ausreißerfilterung (1,5×IQR), multipliziert mit der Fläche dieser Einheit in sqft.',
    adMarketValue: 'Marktwert',
    adMarketLoadFailed: 'Marktdaten konnten nicht geladen werden',
    adRentalValue: 'Mietwert',
    adRentalLoadFailed: 'Mietdaten konnten nicht geladen werden',
    adEstimateWithheld: 'Schätzung zurückgehalten — unzureichende DLD-Daten',
    adEstimateWithheldYield: 'Schätzung zurückgehalten — Rendite-Anomalie (>10 %), wahrscheinlich Datenfehler',
    adVsPurchasePrice: 'gegenüber Kaufpreis',
    adUnrealizedGain: 'Nicht realisierter Gewinn',
    adBasedOn: 'Basierend auf',
    adTransaction: 'Transaktion',
    adTransactions: 'Transaktionen',
    adEjariContract: 'Ejari-Vertrag',
    adEjariContracts: 'Ejari-Verträgen',
    adRecentComparables: 'Aktuelle Vergleichswerte',
    adRecentEjari: 'Aktuelle Ejari-Verträge',
    adColDate: 'Datum',
    adColPriceAed: 'Preis (AED)',
    adColType: 'Typ',
    adColMonthly: 'Monatlich',
    adColAnnual: 'Jährlich',
    adOffPlan: 'Off-Plan',
    adRenewal: 'Verlängerung',
    adSourceDld: 'Quelle: Dubai Land Department · Ausschließlich DLD-Methodik · Keine manuellen oder externen Schätzungen',
    adSourceEjari: 'Quelle: DLD Ejari · Ausschließlich DLD-Methodik · Keine manuellen oder externen Schätzungen',
    adMinPerYr: 'Min./Jahr',
    adMedianPerYr: 'Median/Jahr',
    adMaxPerYr: 'Max./Jahr',
    adRentPerSqftYr: 'Miete/sqft/Jahr',
    adImpliedGrossYield: 'Implizite Bruttorendite',
    adPerMonth: '/Mon.',
    adPerYear: '/Jahr',
    adYoY: 'gg. Vorjahr',
    adNoDldDataForUnit: 'Für diese Einheit liegen keine DLD-Daten vor',
    adNoMatchingEjari: 'Keine passenden DLD-Ejari-Verträge',
    adProjectTimeline: 'Projekt-Zeitplan',
    adTimelineLoadFailed: 'Projekt-Zeitplan konnte nicht geladen werden',
    adTimelineUnavailable: 'Für dieses Projekt liegen keine Zeitplandaten vor. Möglicherweise ist das Projekt nicht im DLD-Mashrooi-System registriert.',
    adComplete: 'abgeschlossen',
    adProjectNo: 'Projekt-Nr.',
    adTotalArea: 'Gesamtfläche',
    adEscrow: 'Treuhandkonto',
    adSourceMashrooi: 'Quelle: DLD Mashrooi · Projekt:',

    // ---------- Benchmark: Vergleich dieser Einheit ----------
    bmTitle: 'Wie diese Einheit im Vergleich liegt',
    bmSubtitlePre: 'Registrierte DLD-Verkäufe, letzte',
    bmSubtitlePost: 'Monate',
    bmUnavailable: 'Es liegen noch keine registrierten Verkäufe für einen Vergleich vor.',
    bmThisUnit: 'Diese Einheit',
    bmBuilding: 'Dieses Gebäude',
    bmCommunity: 'Umgebung',
    bmFromValuation: 'aus der DLD-Bewertung',
    bmFromPurchase: 'aus Ihrem Kaufpreis — noch keine DLD-Bewertung',
    bmSale: 'Verkauf',
    bmSales: 'Verkäufe',
    bmBasisBeds: 'gleiche Zimmeranzahl',
    bmBasisAllTypes: 'alle Wohnungstypen',
    bmTooFew: 'zu wenige registrierte Verkäufe für einen Vergleich',
    bmVerdictAbove: 'über dem Median des Gebäudes',
    bmVerdictBelow: 'unter dem Median des Gebäudes',
    bmVerdictInLine: 'Im Rahmen des Gebäudes',
    bmVsBuilding: 'beim Preis pro sqft',
    bm3mVs3m: 'letzte 3 Monate vs. vorherige 3',
    bmOffplanNote: 'Nur mit Off-Plan-Verkäufen verglichen.',
    bmReadyNote: 'Nur mit Verkäufen fertiggestellter Immobilien verglichen.',
    bmMinCompsPre: 'Ein Median wird erst ab',
    bmMinCompsPost: 'Verkäufen angezeigt.',
    bmEarnsTitle: 'Was sie einbringt',
    bmGrossYield: 'Bruttorendite',
    bmNetYield: 'Nettorendite',
    bmAfterServiceCharge: 'Netto nach Servicegebühr von',
    bmNoServiceCharge: 'Für dieses Gebäude ist keine Servicegebühr erfasst, daher wird keine Nettorendite angezeigt.',
    bmYear: 'Jahr',
    bmSource: 'Quelle',
    bmLiquidityTitle: 'Wie häufig gehandelt wird',
    bmInThisBuilding: 'in diesem Gebäude in 12 Monaten',
    bmPerQuarter: 'pro Quartal',
    bmInTheCommunity: 'in der Umgebung',
    bmAreaRentTitle: 'Mietniveau in der Umgebung',
    bmEjariContracts: 'Ejari-Verträge',
    bmSupplyTitle: 'Neues Angebot in dieser Umgebung',
    bmSupplyUnits: 'Einheiten',
    bmSupplyAcross: 'in',
    bmSupplyProject: 'registriertes Projekt',
    bmSupplyProjects: 'registrierten Projekten',
    bmSupplyWithin: 'mit Fertigstellung innerhalb von',
    bmSupplyNext: 'Als nächstes',
    adMilestoneBookingStarted: 'Buchung gestartet',
    adMilestoneConstructionStarted: 'Baubeginn',
    adMilestoneConstructionProgress: 'Baufortschritt',
    adMilestoneExpectedCompletion: 'Voraussichtliche Fertigstellung',
    adValuationTitle: 'Asset Desk Bewertung',
    adPoweredByDld: 'Basierend auf den offiziellen Registern des Dubai Land Department',
    adRefresh: 'Aktualisieren',

    // ---------- InstallmentPlanView ----------
    iplTotal: 'Gesamt',
    iplMonth: 'Monat',
    iplTapStatusToChange: 'Status antippen, um ihn zu ändern',
    iplTapToChangeStatus: 'Antippen, um den Status zu ändern',
    iplPastPaymentsSummary: 'Übersicht vergangener Zahlungen',

    // ---------- PartnerView ----------
    ptnVvipCooperationPartner: 'VVIP – Kooperationspartner',
    ptnCommissionStatement: 'Provisionsabrechnung',
    ptnSalutationMr: 'Herr',
    ptnSalutationMs: 'Frau',
    ptnSalutationMrs: 'Frau (verh.)',
    ptnSalutationDr: 'Dr.',

    // ---------- keys PropertyView already referenced but no dictionary defined ----------
    notifyWhenReady: 'Wir informieren Sie, sobald diese Funktion verfügbar ist.',
    propertySize: 'Wohnfläche',
    projectTimeline: 'Projekt-Zeitplan',
    bookingStarted: 'Buchung gestartet',
    constructionStarted: 'Baubeginn',
    expectedCompletion: 'Voraussichtliche Fertigstellung',
    pricePerSqft: 'Preis/Fläche',
    exchangeRateNote: 'EUR-Werte sind Näherungswerte (1 AED ≈ 0,25 EUR). Die tatsächlichen Kurse können abweichen.',
    unitDocs: 'Einheit',
    requestNewUnit: 'Neue Einheit anfordern',
    availableDoc: 'Welches Dokument liegt Ihnen vor?',
    selectDocument: '-- Dokument auswählen --',
    additionalNotes: 'Zusätzliche Anmerkungen (optional)',
    notesPlaceholder: 'z. B. Bauträger: Avenew Development, 2-Zimmer-Wohnung',
    selectDocType: 'Bitte wählen Sie aus, welches Dokument Ihnen vorliegt',
    sendRequest: 'Anfrage senden',
  },
};
